import React, { useMemo, useState } from "react";
import { auth, storage } from "./firebase";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { optimizeImageForUpload } from "./imageUploadUtils";
import { assertStorageReady, formatUploadError } from "./uploadErrorUtils";
import { StatCard, ToneBadge } from "./petaarUi";

const VET_OPS_URL =
  process.env.REACT_APP_VET_OPS_URL ||
  "https://us-central1-petaar-3dcab.cloudfunctions.net/vetOps";

function parseCommaList(value) {
  return String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

async function callVetOps(payload) {
  const token = await auth.currentUser?.getIdToken();
  if (!token) throw new Error("Session expired. Please login again.");
  const res = await fetch(VET_OPS_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.success) {
    throw new Error(data?.error || `Vet API failed (${res.status})`);
  }
  return data;
}

async function uploadVetFile(file, options = {}) {
  const {
    maxWidth = 1800,
    maxHeight = 1800,
    maxOutputBytes = 1200 * 1024,
  } = options;
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error("Login expired. Please login again.");
  await assertStorageReady(storage?.app?.options?.storageBucket || "");
  const shouldOptimizeImage = String(file.type || "").toLowerCase().startsWith("image/");
  const uploadFile = shouldOptimizeImage
    ? await optimizeImageForUpload(file, {
      maxWidth,
      maxHeight,
      quality: 0.82,
      maxOutputBytes,
      skipBelowBytes: 260 * 1024,
      outputType: "image/jpeg",
    })
    : file;
  const safeName = String(uploadFile.name || file.name || "document")
    .toLowerCase()
    .replace(/[^a-z0-9._-]/g, "-")
    .slice(0, 80);
  const filePath = `vetDocuments/${uid}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${safeName}`;
  const fileRef = ref(storage, filePath);
  await uploadBytes(fileRef, uploadFile, {
    contentType: uploadFile.type || file.type || "application/octet-stream",
    cacheControl: "private,max-age=0,no-store",
  });
  const url = await getDownloadURL(fileRef);
  return {
    name: uploadFile.name || file.name || safeName,
    mimeType: uploadFile.type || file.type || "application/octet-stream",
    size: Number(uploadFile.size) || Number(file.size) || 0,
    url,
  };
}

function verificationTone(status) {
  const value = String(status || "").toLowerCase();
  if (value === "approved") return "success";
  if (value === "rejected") return "danger";
  if (value === "pending") return "warning";
  return "default";
}

function availabilityTone(status) {
  return String(status || "").toLowerCase() === "online" ? "success" : "default";
}

export default function VetOnboardingPanel({ userProfile = {}, onUpdated }) {
  const [form, setForm] = useState({
    displayName: userProfile.displayName || "",
    yearsOfService: userProfile.yearsExperience || "",
    currentWorkplace: "",
    location: userProfile.city || "",
    registrationNumber: "",
    councilName: "",
    specializations: "",
    languages: "",
    whatsappNumber: userProfile.phone || "",
    callingNumber: userProfile.phone || "",
    consultationModes: {
      chat: true,
      audio: true,
      video: true,
    },
  });
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [profilePhotoUploading, setProfilePhotoUploading] = useState(false);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState("");
  const [documents, setDocuments] = useState([]);
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [availabilityUpdating, setAvailabilityUpdating] = useState(false);

  const verificationStatus = String(userProfile.vetVerificationStatus || "not_submitted");
  const availabilityStatus = String(userProfile.vetAvailabilityStatus || "offline");
  const isApprovedVet = verificationStatus === "approved";

  const consultationModes = useMemo(() => (
    Object.entries(form.consultationModes || {})
      .filter(([, checked]) => Boolean(checked))
      .map(([mode]) => mode)
  ), [form.consultationModes]);

  const completion = useMemo(() => {
    let score = 0;
    if (String(form.displayName || "").trim()) score += 1;
    if (String(form.currentWorkplace || "").trim()) score += 1;
    if (String(form.location || "").trim()) score += 1;
    if (String(form.registrationNumber || "").trim()) score += 1;
    if (consultationModes.length) score += 1;
    if (profilePhotoUrl) score += 1;
    if (documents.length) score += 1;
    return Math.round((score / 7) * 100);
  }, [consultationModes.length, documents.length, form.currentWorkplace, form.displayName, form.location, form.registrationNumber, profilePhotoUrl]);

  const onUploadProfilePhoto = async (file) => {
    if (!file) return;
    const maxBytes = 10 * 1024 * 1024;
    if (!String(file.type || "").startsWith("image/")) {
      alert("Profile photo must be an image.");
      return;
    }
    if ((Number(file.size) || 0) > maxBytes) {
      alert("Profile photo must be under 10MB.");
      return;
    }
    try {
      setProfilePhotoUploading(true);
      const uploaded = await uploadVetFile(file, {
        maxWidth: 1400,
        maxHeight: 1400,
        maxOutputBytes: 550 * 1024,
      });
      setProfilePhotoUrl(uploaded.url);
      setProfilePhoto(uploaded);
    } catch (error) {
      alert(formatUploadError(error, storage?.app?.options?.storageBucket));
    } finally {
      setProfilePhotoUploading(false);
    }
  };

  const onUploadDocuments = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const maxBytes = 12 * 1024 * 1024;
    const validFiles = files.filter((file) => {
      const type = String(file.type || "").toLowerCase();
      const validType = type.startsWith("image/") || type === "application/pdf";
      return validType && (Number(file.size) || 0) <= maxBytes;
    });
    if (!validFiles.length) {
      alert("Use image/PDF files under 12MB.");
      return;
    }
    if (validFiles.length < files.length) {
      alert("Some files were skipped (only image/PDF under 12MB are accepted).");
    }
    try {
      setUploadingDocs(true);
      const uploaded = await Promise.all(validFiles.map((file) => uploadVetFile(file, {
        maxWidth: 2100,
        maxHeight: 2100,
        maxOutputBytes: 1300 * 1024,
      })));
      setDocuments((prev) => [...prev, ...uploaded].slice(0, 12));
    } catch (error) {
      alert(formatUploadError(error, storage?.app?.options?.storageBucket));
    } finally {
      setUploadingDocs(false);
    }
  };

  const submitApplication = async () => {
    if (submitting) return;
    if (!form.registrationNumber || !form.currentWorkplace || !form.location) {
      alert("Please fill registration number, current workplace, and location.");
      return;
    }
    if (!profilePhotoUrl) {
      alert("Please upload your profile photo.");
      return;
    }
    if (!documents.length) {
      alert("Please upload veterinary certificate and verification documents.");
      return;
    }
    try {
      setSubmitting(true);
      const payload = {
        action: "submit_application",
        vetProfile: {
          displayName: form.displayName,
          yearsOfService: Number(form.yearsOfService) || 0,
          currentWorkplace: form.currentWorkplace,
          location: form.location,
          registrationNumber: form.registrationNumber,
          councilName: form.councilName,
          specializations: parseCommaList(form.specializations),
          languages: parseCommaList(form.languages),
          whatsappNumber: form.whatsappNumber,
          callingNumber: form.callingNumber,
          consultationModes,
          profilePhotoUrl,
        },
        documents,
      };
      const result = await callVetOps(payload);
      if (result.autoRejected) {
        alert("Application rejected: uploaded certificates look AI-generated/non-authentic. Please re-upload genuine documents.");
      } else {
        alert("Vet application submitted successfully. Admin review is pending.");
      }
      onUpdated?.();
    } catch (error) {
      alert(error.message || "Unable to submit application.");
    } finally {
      setSubmitting(false);
    }
  };

  const setAvailability = async (status) => {
    if (availabilityUpdating) return;
    try {
      setAvailabilityUpdating(true);
      await callVetOps({ action: "set_availability", status });
      onUpdated?.();
    } catch (error) {
      alert(error.message || "Unable to update vet availability.");
    } finally {
      setAvailabilityUpdating(false);
    }
  };

  return (
    <section className="petaar-card" style={{ display: "grid", gap: 24 }}>
      <div className="petaar-section-title">
        <div>
          <div className="petaar-kicker">Vet Verification</div>
          <h3 style={{ fontSize: "1.65rem" }}>Consultation setup</h3>
          <p style={{ margin: "8px 0 0", color: "var(--muted)", lineHeight: 1.6 }}>
            Submit KYC once, get reviewed by admin, then control your online status for instant consultations.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <ToneBadge tone={verificationTone(verificationStatus)}>{verificationStatus.replaceAll("_", " ")}</ToneBadge>
          <ToneBadge tone={availabilityTone(availabilityStatus)}>{availabilityStatus}</ToneBadge>
        </div>
      </div>

      <div className="petaar-stat-grid">
        <StatCard label="Profile readiness" value={completion} suffix="%" tone="accent" meta="Based on KYC completeness" />
        <StatCard label="Consultation modes" value={consultationModes.length} tone="default" meta={consultationModes.join(", ") || "None selected"} />
        <StatCard label="Documents uploaded" value={documents.length} tone="success" meta="Up to 12 verification files" />
        <StatCard label="Availability" value={availabilityStatus === "online" ? 1 : 0} tone={availabilityStatus === "online" ? "success" : "warning"} meta={availabilityStatus === "online" ? "Visible to owners now" : "Not accepting instant consults"} />
      </div>

      <div className="petaar-split" style={{ alignItems: "start" }}>
        <div style={{ display: "grid", gap: 18 }}>
          <div className="petaar-form-grid">
            <input
              placeholder="Display name"
              value={form.displayName}
              onChange={(e) => setForm((prev) => ({ ...prev, displayName: e.target.value }))}
            />
            <input
              placeholder="Years of service"
              value={form.yearsOfService}
              onChange={(e) => setForm((prev) => ({ ...prev, yearsOfService: e.target.value }))}
            />
            <input
              placeholder="Current workplace *"
              value={form.currentWorkplace}
              onChange={(e) => setForm((prev) => ({ ...prev, currentWorkplace: e.target.value }))}
            />
            <input
              placeholder="Location *"
              value={form.location}
              onChange={(e) => setForm((prev) => ({ ...prev, location: e.target.value }))}
            />
            <input
              placeholder="Vet registration number *"
              value={form.registrationNumber}
              onChange={(e) => setForm((prev) => ({ ...prev, registrationNumber: e.target.value }))}
            />
            <input
              placeholder="Council / board"
              value={form.councilName}
              onChange={(e) => setForm((prev) => ({ ...prev, councilName: e.target.value }))}
            />
            <input
              placeholder="Specializations (comma separated)"
              value={form.specializations}
              onChange={(e) => setForm((prev) => ({ ...prev, specializations: e.target.value }))}
            />
            <input
              placeholder="Languages (comma separated)"
              value={form.languages}
              onChange={(e) => setForm((prev) => ({ ...prev, languages: e.target.value }))}
            />
            <input
              placeholder="WhatsApp number"
              value={form.whatsappNumber}
              onChange={(e) => setForm((prev) => ({ ...prev, whatsappNumber: e.target.value }))}
            />
            <input
              placeholder="Calling number"
              value={form.callingNumber}
              onChange={(e) => setForm((prev) => ({ ...prev, callingNumber: e.target.value }))}
            />
          </div>

          <div className="petaar-card" style={{ padding: 18, display: "grid", gap: 12, background: "var(--surface-alt)" }}>
            <div className="petaar-kicker">Consultation Modes</div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {["chat", "audio", "video"].map((mode) => {
                const active = Boolean(form.consultationModes[mode]);
                return (
                  <label
                    key={mode}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "10px 14px",
                      borderRadius: 999,
                      border: active ? "1px solid rgba(26, 26, 219, 0.22)" : "1px solid var(--border)",
                      background: active ? "rgba(26, 26, 219, 0.08)" : "#fff",
                      color: active ? "var(--primary)" : "var(--text)",
                      fontWeight: 700,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={(e) => setForm((prev) => ({
                        ...prev,
                        consultationModes: {
                          ...(prev.consultationModes || {}),
                          [mode]: e.target.checked,
                        },
                      }))}
                    />
                    {mode}
                  </label>
                );
              })}
            </div>
          </div>

          <div className="petaar-card" style={{ padding: 18, display: "grid", gap: 14, background: "var(--surface-alt)" }}>
            <div>
              <div className="petaar-kicker">Profile Photo</div>
              <h4 style={{ fontSize: "1.1rem" }}>Required for owner-facing profile</h4>
            </div>
            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <label className="petaar-button petaar-button--secondary" htmlFor="vet-profile-photo" style={{ cursor: profilePhotoUploading ? "not-allowed" : "pointer" }}>
                {profilePhotoUploading ? "Uploading..." : "Choose Photo"}
              </label>
              <input
                id="vet-profile-photo"
                type="file"
                accept="image/*"
                onChange={(e) => onUploadProfilePhoto(e.target.files?.[0])}
                disabled={profilePhotoUploading}
                style={{ display: "none" }}
              />
              <span style={{ color: "var(--muted)" }}>{profilePhoto ? profilePhoto.name : "No photo uploaded"}</span>
            </div>
            {profilePhotoUrl ? (
              <img
                src={profilePhotoUrl}
                alt="Vet profile"
                style={{ width: 96, height: 96, objectFit: "cover", borderRadius: 20, border: "1px solid var(--border)" }}
              />
            ) : null}
          </div>

          <div className="petaar-card" style={{ padding: 18, display: "grid", gap: 14, background: "var(--surface-alt)" }}>
            <div>
              <div className="petaar-kicker">Verification Documents</div>
              <h4 style={{ fontSize: "1.1rem" }}>Required documents</h4>
              <p style={{ margin: "6px 0 0", color: "var(--muted)", lineHeight: 1.6 }}>
                Upload veterinary certificate, registration certificate, government ID, and any supporting documents.
              </p>
            </div>
            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <label className="petaar-button petaar-button--secondary" htmlFor="vet-documents" style={{ cursor: uploadingDocs ? "not-allowed" : "pointer" }}>
                {uploadingDocs ? "Uploading..." : "Choose Files"}
              </label>
              <input
                id="vet-documents"
                type="file"
                accept="image/*,.pdf,application/pdf"
                multiple
                onChange={(e) => {
                  onUploadDocuments(e.target.files);
                  e.target.value = "";
                }}
                disabled={uploadingDocs}
                style={{ display: "none" }}
              />
              <span style={{ color: "var(--muted)" }}>{documents.length} file(s) ready</span>
            </div>
            <div style={{ display: "grid", gap: 10 }}>
              {documents.map((item, index) => (
                <div key={`${item.url}-${index}`} className="petaar-list-row" style={{ padding: 14, display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis" }}>{item.name}</div>
                    <div style={{ marginTop: 4, color: "var(--muted)", fontSize: "0.92rem" }}>{item.mimeType} · {(Number(item.size || 0) / 1024).toFixed(0)} KB</div>
                  </div>
                  <button className="petaar-button petaar-button--ghost" onClick={() => setDocuments((prev) => prev.filter((_, i) => i !== index))}>
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={{ display: "grid", gap: 18 }}>
          <div className="petaar-card" style={{ display: "grid", gap: 14 }}>
            <div className="petaar-kicker">Submission Checklist</div>
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.8, color: "var(--muted)" }}>
              <li>Use the same registration number as your official certificate.</li>
              <li>Upload real documents only. AI-generated or altered files can be auto-rejected.</li>
              <li>Choose all consultation modes you are actually ready to handle.</li>
              <li>Keep your workplace and city accurate so assignments route correctly.</li>
            </ul>
          </div>

          <div className="petaar-card" style={{ display: "grid", gap: 14 }}>
            <div className="petaar-kicker">Availability</div>
            <h4 style={{ fontSize: "1.2rem" }}>Instant consultation status</h4>
            <p style={{ margin: 0, color: "var(--muted)", lineHeight: 1.6 }}>
              Set yourself online only when you can respond quickly. Owners see only Petaar’s in-app consultation flow, not your direct number.
            </p>
            {!isApprovedVet ? (
              <div className="petaar-notice info">Go online is enabled only after vet verification is approved.</div>
            ) : null}
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button
                className="petaar-button"
                onClick={() => setAvailability("online")}
                disabled={!isApprovedVet || availabilityUpdating || availabilityStatus === "online"}
              >
                {availabilityUpdating && availabilityStatus !== "online" ? "Updating..." : "Go Online"}
              </button>
              <button
                className="petaar-button petaar-button--ghost"
                onClick={() => setAvailability("offline")}
                disabled={!isApprovedVet || availabilityUpdating || availabilityStatus === "offline"}
              >
                {availabilityUpdating && availabilityStatus !== "offline" ? "Updating..." : "Go Offline"}
              </button>
            </div>
          </div>

          <div className="petaar-card" style={{ display: "grid", gap: 12 }}>
            <div className="petaar-kicker">Submit</div>
            <p style={{ margin: 0, color: "var(--muted)", lineHeight: 1.6 }}>
              Submit once after all documents are ready. Admin review decides whether the profile goes live for owner-facing online consultation booking.
            </p>
            <button className="petaar-button" onClick={submitApplication} disabled={submitting || profilePhotoUploading || uploadingDocs}>
              {submitting ? "Submitting..." : "Submit Vet Verification"}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
